import React, { useEffect } from 'react';
import { useSetState } from '@hpfe/common-base';
import classNames from 'classnames';
import { useLocation } from 'react-router';

import style from './style.module.less';
import { apiDispatchAuthInformation, PermissionCode } from '@/apis/auth';
import type { IResource } from '@/apis/auth';
import { OrderBusinessType } from '@/components/enum';
import { GlobalWatermark } from '@/components/global-watermark/global-watermark';
import { PageContent } from '@/components/layout/admin/components/page-content';
import { SideBar } from '@/components/layout/admin/components/side-bar';
import { countryCode } from '@/config/country';
import { context } from '@/context';
import { useDispatchWorkbenchStore } from '@/stores/dispatch-workbench';

/**
 * AI.GEN lines=1-95
 * 管理后台布局组件 - 应用缩放功能与全局水印
 */
export const LayoutAdmin = () => {
  const location = useLocation();
  const zoom = useDispatchWorkbenchStore((state) => state.zoom);
  const { orderBusinessType, setOrderBusinessType } =
    useDispatchWorkbenchStore();
  const { state: watermarkState, setState: setWatermarkState } = useSetState<{
    userName: string | null;
    userId: number | null;
  }>({
    userName: null,
    userId: null,
  });

  // 应用缩放功能
  useEffect(() => {
    const docEl = document.documentElement;
    docEl.style.cssText = `zoom:${zoom}%`;
  }, [zoom]);

  // 获取用户信息并根据权限设置订单业务类型
  useEffect(() => {
    const hasPermission = (resources: IResource[], code: string): boolean => {
      for (const item of resources) {
        if (item.code === code) {
          return true;
        }

        if (item.children?.length && hasPermission(item.children, code)) {
          return true;
        }
      }
      return false;
    };

    const fetchUserInfo = async () => {
      try {
        const res = await apiDispatchAuthInformation();
        const resources = res.resources ?? [];

        // 存储扩展用户信息到 context
        context.user.setExtendedUserInfo({
          userNick: res.userNick ?? null,
          resources,
          cities: null, // 该接口不返回城市列表
        });

        setWatermarkState({
          userName: res.userName ?? null,
          userId: res.userId ?? null,
        });

        // 根据用户权限设置订单业务类型（参考旧项目的 getOrderBusinessType 逻辑）
        const filteredOrderBusinessType: OrderBusinessType[] = [];

        if (
          hasPermission(resources, PermissionCode.HP_ORDER) &&
          orderBusinessType.includes(OrderBusinessType.HP)
        ) {
          filteredOrderBusinessType.push(OrderBusinessType.HP);
        }
        if (
          hasPermission(resources, PermissionCode.PF_ORDER) &&
          orderBusinessType.includes(OrderBusinessType.PF)
        ) {
          filteredOrderBusinessType.push(OrderBusinessType.PF);
        }

        // 过滤后与当前不同则更新（包括过滤后为空的情况）
        if (
          JSON.stringify(filteredOrderBusinessType.sort()) !==
          JSON.stringify([...orderBusinessType].sort())
        ) {
          setOrderBusinessType(filteredOrderBusinessType);
        }
      } catch (error) {
        // 静默失败，使用缓存数据
      }
    };

    // 每次组件挂载时都获取用户信息（确保数据是最新的）
    fetchUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={style.reactAdmin}>
      <GlobalWatermark
        countryCode={countryCode}
        userId={watermarkState.userId}
        userName={watermarkState.userName}
      >
        {!location.search.includes('sourceSiteUri=1') ? (
          <>
            <SideBar></SideBar>
            <div className={classNames(style.content, 'flex-column')}>
              <PageContent></PageContent>
            </div>
          </>
        ) : (
          <PageContent></PageContent>
        )}
      </GlobalWatermark>
    </div>
  );
};
